<template>
     <!-- Header -->
    <div class="bg-white shadow-sm border-b border-gray-200 px-6 py-4 flex justify-between items-center">
      <h1 class="text-2xl font-bold text-gray-900">Appeals & Donations Management</h1>
      <div class="flex items-center">
        <div class="relative mr-3">
          <span
            class="absolute top-0 right-0 -mt-1 -mr-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">3</span>
          <button class="text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 01-6 0v-1m6 0H9" />
            </svg>
          </button>
        </div>
        <button @click="dropdownOpen = !dropdownOpen"
          class="relative flex items-center w-18 h-12 overflow-hidden focus:outline-none">
          <img src="@/assets/images/avatar.png" alt="profile" class="w-10 h-10 rounded-full object-cover mr-2" />
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24"
            stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div v-show="dropdownOpen" @click="dropdownOpen = false" class="fixed inset-0 z-10 w-full h-full"></div>
        <transition enter-active-class="transition duration-150 ease-out transform"
          enter-from-class="scale-95 opacity-0" enter-to-class="scale-100 opacity-100"
          leave-active-class="transition duration-150 ease-in transform" leave-from-class="scale-100 opacity-100"
          leave-to-class="scale-95 opacity-0">
          <div v-show="dropdownOpen" class="absolute right-0 z-20 w-48 py-2 mt-40 bg-white rounded-md shadow-xl">
            <button href="#"
              class="block px-4 py-2 text-sm w-full text-gray-700 hover:bg-blue-600 hover:text-orange-400"
              @click="redirectProfilePage">Profile</button>
            <button class="block px-4 py-2 w-full text-sm text-gray-700 hover:bg-blue-600 hover:text-orange-400"
              @click="logout">
              Log out
            </button>
          </div>
        </transition>
      </div>
    </div>
  <div class="min-h-screen bg-gray-50 p-6">
    <div class="max-w-7xl mx-auto">
      <!-- Search and Filter Section -->
      <div class="flex items-center justify-between gap-4 mb-6">
        <div class="flex items-center gap-4 flex-1">
        <!-- Filters Dropdown -->
        <div class="relative">
          <button
            @click="showFilters = !showFilters"
            class="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"></path>
            </svg>
            <span class="font-medium text-gray-700">Filters</span>
            <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
        </div>

        <!-- Search Bar -->
        <div class="flex flex-1 max-w-md">
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search"
            class="flex-1 px-4 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
          />
          <button class="px-6 py-2 bg-orange-500 text-white rounded-r-lg hover:bg-orange-600 transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </button>
        </div>
        </div>

        <button
          v-if="activeTab === 'ads'"
          type="button"
          class="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap"
          @click="addCampaign"
        >
          <span class="text-lg leading-none">+</span>
          <span class="font-medium">Add Campaign</span>
        </button>
      </div>

      <!-- Statistics Cards -->
      <div :class="['grid gap-6 mb-6', activeTab === 'pending' ? 'grid-cols-3' : 'grid-cols-1']">
        <div v-for="stat in currentStats" :key="stat.label" :class="stat.bgClass + ' rounded-lg shadow p-6'">
          <p class="text-gray-600 text-sm mb-2">{{ stat.label }}</p>
          <p class="text-4xl font-bold text-gray-800">{{ stat.value }}</p>
        </div>
      </div>

      <!-- Tabs -->
      <div class="flex gap-8 border-b border-gray-200 mb-6">
        <button
          @click="activeTab = 'pending'"
          :class="[
            'pb-3 font-semibold transition-colors relative',
            activeTab === 'pending'
              ? 'text-blue-900 border-b-2 border-blue-900'
              : 'text-gray-500 hover:text-gray-700'
          ]"
        >
          Appeals
        </button>
        <button
          @click="activeTab = 'donations'"
          :class="[
            'pb-3 font-semibold transition-colors relative',
            activeTab === 'donations'
              ? 'text-blue-900 border-b-2 border-blue-900'
              : 'text-gray-500 hover:text-gray-700'
          ]"
        >
          Donations
        </button>
        <button
          @click="activeTab = 'ads'"
          :class="[
            'pb-3 font-semibold transition-colors relative',
            activeTab === 'ads'
              ? 'text-blue-900 border-b-2 border-blue-900'
              : 'text-gray-500 hover:text-gray-700'
          ]"
        >
          Ads
        </button>
      </div>

      <!-- Tab Content -->
      <AppealsTab
        v-if="activeTab === 'pending'"
        :search-query="searchQuery"
        @update-stats="updateAppealsStats"
      />
      <DonationsTab
        v-else-if="activeTab === 'donations'"
        :search-query="searchQuery"
        @update-stats="updateDonationsStats"
      />
      <AdsTab
        v-else
        :search-query="searchQuery"
        @update-stats="updateAdsStats"
      />
    </div>
  </div>

  <!-- Add Campaign Modal -->
  <transition
    enter-active-class="transition ease-out duration-200"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition ease-in duration-150"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="showAddCampaignModal"
      class="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-40"
    >
      <div
        class="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
        @click.stop
      >
        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 class="text-lg font-semibold text-gray-900">Add New Campaign</h2>
          <button
            type="button"
            class="text-gray-400 hover:text-gray-600"
            @click="closeCampaignModal"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form class="px-6 py-5 space-y-4" @submit.prevent="submitCampaign">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Company Name <span class="text-red-500">*</span>
              </label>
              <input
                v-model="campaignForm.company_name"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none text-sm"
                placeholder="Enter company name"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Contact Person <span class="text-red-500">*</span>
              </label>
              <input
                v-model="campaignForm.contact_person"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none text-sm"
                placeholder="Contact person"
              />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Contact Number <span class="text-red-500">*</span>
              </label>
              <input
                v-model="campaignForm.contact_number"
                type="tel"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none text-sm"
                placeholder="+254 700 000000"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Contact Email <span class="text-red-500">*</span>
              </label>
              <input
                v-model="campaignForm.contact_email"
                type="email"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none text-sm"
                placeholder="name@example.com"
              />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Ad Link <span class="text-red-500">*</span>
              </label>
              <input
                v-model="campaignForm.ad_link"
                type="url"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none text-sm"
                placeholder="https://example.com/ad"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                v-model="campaignForm.status"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none text-sm bg-white"
              >
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="paused">Paused</option>
              </select>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Ad Wording <span class="text-red-500">*</span>
            </label>
            <textarea
              v-model="campaignForm.ad_wording"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none text-sm resize-none"
              placeholder="Enter the ad copy here..."
            ></textarea>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Ad Image
            </label>
            <div
              class="border-2 border-dashed border-gray-300 rounded-lg px-4 py-6 flex flex-col items-center justify-center text-center cursor-pointer hover:border-orange-400 hover:bg-orange-50/40 transition-colors"
              @click="$refs.adImageInput.click()"
            >
              <svg class="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p class="text-sm text-gray-700">
                <span class="font-medium text-orange-600">Click to upload ad image</span>
              </p>
              <p class="text-xs text-gray-500 mt-1">
                PNG, JPG up to ~2MB
              </p>
              <p v-if="adImageName" class="mt-2 text-xs text-gray-600">
                Selected: <span class="font-medium">{{ adImageName }}</span>
              </p>
              <input
                ref="adImageInput"
                type="file"
                accept="image/*"
                class="hidden"
                @change="onAdImageSelected"
              />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Start Date <span class="text-red-500">*</span>
              </label>
              <input
                v-model="campaignForm.start_date"
                type="date"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none text-sm"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                End Date <span class="text-red-500">*</span>
              </label>
              <input
                v-model="campaignForm.end_date"
                type="date"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none text-sm"
              />
            </div>
          </div>

          <p v-if="createCampaignError" class="text-sm text-red-600">
            {{ createCampaignError }}
          </p>

          <div class="flex items-center justify-end gap-3 pt-2 pb-4">
            <button
              type="button"
              class="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50"
              :disabled="creatingCampaign"
              @click="closeCampaignModal"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-5 py-2 rounded-lg bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed"
              :disabled="creatingCampaign"
            >
              <span v-if="creatingCampaign">Adding...</span>
              <span v-else>Add Campaign</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </transition>
</template>

<script>
import AppealsTab from '@/components/tabs/AppealsTab.vue';
import AdsTab from '@/components/tabs/AdsTab.vue';
import { AdsAPI } from '@/services/ads';
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { useToast } from 'vue-toastification';
import DonationsTab from '@/components/tabs/DonationsTab.vue';

export default {
  name: 'AppealsDonations',
  components: {
    AppealsTab,
    AdsTab,
    DonationsTab
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      dropdownOpen: false,
      showFilters: false,
      searchQuery: '',
      activeTab: 'pending',
      showAddCampaignModal: false,
      creatingCampaign: false,
      createCampaignError: '',
      campaignForm: {
        company_name: '',
        contact_person: '',
        contact_number: '',
        contact_email: '',
        ad_link: '',
        ad_wording: '',
        ad_image: '',
        status: 'active',
        start_date: '',
        end_date: ''
      },
      adImageName: '',
      adImageFile: null,
      appealsStats: {
        allAppeals: 50,
        pendingAppeals: 40,
        closedAppeals: 5
      },
      adsStats: {
        totalAds: 0
      },
      donationsStats: {
        totalDonations: 0
      }
    };
  },
  computed: {
    currentStats() {
      if (this.activeTab === 'pending') {
        return [
          { label: 'All Appeals', value: this.appealsStats.allAppeals, bgClass: 'bg-white' },
          { label: 'Pending Appeals', value: this.appealsStats.pendingAppeals, bgClass: 'bg-red-50' },
          { label: 'Closed Appeals', value: this.appealsStats.closedAppeals, bgClass: 'bg-green-50' }
        ];
      } else if (this.activeTab === 'ads') {
        return [
          { label: 'Total Ads', value: this.adsStats.totalAds, bgClass: 'bg-white' }
        ];
      }
      return [
        { label: 'Total Donation Requests', value: this.donationsStats.totalDonations, bgClass: 'bg-white' }
      ];
    }
  },
  methods: {
    updateAppealsStats(stats) {
      this.appealsStats = stats;
    },
    updateAdsStats(stats) {
      this.adsStats = stats;
    },
    updateDonationsStats(stats) {
      this.donationsStats = stats;
    },
    addCampaign() {
      this.resetCampaignForm();
      this.showAddCampaignModal = true;
    },
    closeCampaignModal() {
      if (this.creatingCampaign) return;
      this.showAddCampaignModal = false;
    },
    resetCampaignForm() {
      this.campaignForm = {
        company_name: '',
        contact_person: '',
        contact_number: '',
        contact_email: '',
        ad_link: '',
        ad_wording: '',
        ad_image: '',
        status: 'active',
        start_date: '',
        end_date: ''
      };
      this.adImageName = '';
      this.adImageFile = null;
      this.createCampaignError = '';
    },
    onAdImageSelected(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        this.createCampaignError = 'Please upload a JPG, PNG, SVG, or WEBP image.';
        this.toast.error('Unsupported image format. Please upload a JPG, PNG, SVG, or WEBP image.');
        return;
      }

      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        this.createCampaignError = 'Image size should not exceed 10MB.';
        this.toast.error('Image size should not exceed 10MB.');
        return;
      }

      this.adImageName = file.name;
      this.adImageFile = file;
      this.createCampaignError = '';
      this.toast.success('Ad image uploaded successfully');
    },
    async uploadAdImage(file) {
      if (!file) {
        return '';
      }

      const accessKeyId = process.env.VUE_APP_S3_ACCESS_KEY_ID || 'DO00PK2KLQ4HUBKTB6R3';
      const secretAccessKey = process.env.VUE_APP_S3_SECRET_ACCESS_KEY || 'L3yOKmH3Dqfpc3tVZv4Mz5O+jd1bXTobKS//zmY72Rk';

      const s3 = new S3Client({
        region: 'blr1',
        credentials: {
          accessKeyId,
          secretAccessKey
        },
        endpoint: 'https://blr1.digitaloceanspaces.com'
      });

      const key = `ads/${Date.now()}_${file.name}`;
      const fileBuffer = new Uint8Array(await file.arrayBuffer());

      const params = {
        Bucket: 'jiji-bucket',
        Key: key,
        Body: fileBuffer,
        ACL: 'public-read',
        ContentType: file.type
      };

      try {
        const command = new PutObjectCommand(params);
        await s3.send(command);
        const fileUrl = `https://jiji-bucket.blr1.cdn.digitaloceanspaces.com/${key}`;
        return fileUrl;
      } catch (error) {
        console.error('Ad image upload error:', error);
        this.toast.error('Unable to upload ad image, please check your internet connection.');
        throw new Error(error.message || 'Failed to upload ad image');
      }
    },
    async submitCampaign() {
      this.createCampaignError = '';

      const requiredFields = [
        'company_name',
        'contact_person',
        'contact_number',
        'contact_email',
        'ad_link',
        'ad_wording',
        'start_date',
        'end_date'
      ];

      const missing = requiredFields.filter(field => !this.campaignForm[field]);
      if (missing.length) {
        this.createCampaignError = 'Please fill in all required fields.';
        this.toast.error('Please fill in all required fields.');
        return;
      }

      try {
        this.creatingCampaign = true;

        if (this.adImageFile) {
          this.campaignForm.ad_image = await this.uploadAdImage(this.adImageFile);
        }

        const payload = { ...this.campaignForm };
        await AdsAPI.createAd(payload);
        this.showAddCampaignModal = false;
        this.toast.success('Campaign created successfully');
        // In a real flow you might refresh ads list here
      } catch (error) {
        console.error('Failed to create campaign:', error);
        this.createCampaignError =
          error.response?.data?.message || 'Failed to create campaign. Please try again.';
        this.toast.error(this.createCampaignError);
      } finally {
        this.creatingCampaign = false;
      }
    },
    redirectProfilePage() {
      // Navigate to e-learning dashboard as profile placeholder
      this.$router.push('/e-learning');
    },
    logout() {
      this.$store.dispatch('logout', { system: 'appeals' });
      this.$router.push('/login');
    }
  }
};
</script>

<style scoped>
/* Additional custom styles if needed */
</style>